import React from "react";
import { NavLink } from "react-router-dom";
import { cn } from "../../utils/cn";
import {
  Bot,
  Calendar,
  Music,
  Building2,
  Tag,
  Languages,
  Layers,
  Users,
  Flag,
  Sparkles,
  UserSearch,
  Workflow,
  Monitor,
  Settings,
  LogOut,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

const navigation = [
  { name: "Events", href: "/events", icon: Calendar },
  { name: "Artists", href: "/artists", icon: Music },
  { name: "Venues", href: "/venues", icon: Building2 },
  { name: "Genres", href: "/genres", icon: Tag },
  { name: "Themes", href: "/themes", icon: Layers },
  { name: "Pipeline Runs", href: "/pipeline-runs", icon: Workflow },
  { name: "Curator", href: "/curator", icon: Bot },
  { name: "Users", href: "/users", icon: Users },
  { name: "Reports", href: "/reports", icon: Flag },
  { name: "Event research", href: "/event-research", icon: Sparkles },
  { name: "Artist research", href: "/artist-research", icon: UserSearch },
  { name: "Translations", href: "/translations", icon: Languages },
  { name: "System", href: "/system", icon: Monitor },
  { name: "Settings", href: "/settings", icon: Settings },
];

function LogoutButton({ onLogout, collapsed, className }) {
  return (
    <button
      onClick={onLogout}
      title={collapsed ? "Log out" : undefined}
      className={cn(
        "flex min-w-[4rem] flex-col items-center justify-center gap-1 rounded-lg px-2 py-1.5 text-center text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground lg:w-full lg:min-w-0 lg:flex-row lg:justify-start lg:gap-3 lg:px-3 lg:py-2 lg:text-left lg:text-sm",
        collapsed && "lg:justify-center lg:px-2",
        className
      )}
    >
      <LogOut className="h-5 w-5 flex-shrink-0" />
      <span className={cn("max-w-[4.75rem] truncate leading-tight lg:max-w-none", collapsed && "lg:hidden")}>
        Log out
      </span>
    </button>
  );
}

export function Sidebar({ onLogout, collapsed, onToggle }) {
  return (
    <div
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 flex h-[calc(5rem+env(safe-area-inset-bottom))] border-t border-border bg-sidebar pb-[env(safe-area-inset-bottom)] transition-all duration-200 lg:relative lg:inset-auto lg:h-dvh lg:flex-col lg:border-r lg:border-t-0 lg:pb-0",
        collapsed ? "lg:w-16" : "lg:w-64"
      )}
    >
      {/* Logo */}
      <div className="hidden h-16 items-center justify-between px-4 border-b border-border lg:flex">
        {!collapsed && (
          <span className="text-xl font-bold text-sidebar-foreground">
            Applauss
          </span>
        )}
        <button
          onClick={onToggle}
          className="rounded-md p-1.5 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <ChevronsRight className="h-5 w-5" />
          ) : (
            <ChevronsLeft className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Phone strip scrolls horizontally; the mask fades the right edge as a scroll hint. */}
      <nav className="flex min-w-0 flex-1 items-stretch gap-1 overflow-x-auto px-2 py-2 [mask-image:linear-gradient(to_right,black_calc(100%_-_2rem),transparent)] lg:block lg:space-y-1 lg:px-2 lg:py-4 lg:[mask-image:none]">
        {navigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            title={collapsed ? item.name : undefined}
            className={({ isActive }) =>
              cn(
                "flex min-w-[4rem] flex-col items-center justify-center gap-1 rounded-lg px-2 py-1.5 text-center text-[11px] font-medium transition-colors lg:min-w-0 lg:flex-row lg:justify-start lg:gap-3 lg:px-3 lg:py-2 lg:text-left lg:text-sm",
                collapsed && "lg:justify-center lg:px-2",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )
            }
          >
            <item.icon className="h-5 w-5 flex-shrink-0" />
            <span
              className={cn(
                "max-w-[4.75rem] truncate leading-tight lg:max-w-none",
                collapsed && "lg:hidden"
              )}
            >
              {item.name}
            </span>
          </NavLink>
        ))}
        {/* Rare action: scrolls with the strip instead of holding a fixed slot */}
        <LogoutButton onLogout={onLogout} collapsed={collapsed} className="lg:hidden" />
      </nav>

      {/* Logout (desktop: pinned) */}
      <div className="hidden shrink-0 p-2 lg:block lg:border-t lg:border-border">
        <LogoutButton onLogout={onLogout} collapsed={collapsed} />
      </div>
    </div>
  );
}

export default Sidebar;
